import { useMutation, useQueryClient } from '@tanstack/react-query';

import { authFetch } from '@verantos-dev/core-utils';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { markAnalysisProcessing } from './markAnalysisProcessing';
import { analysisKeys } from './queryKeys';
import type { AnalysisExecution } from './types';

interface ProcessAnalysisInput {
  projectId: string;
  analysisProfileId: string;
  /** The profile's latest saved version — the backend rejects any other version. */
  analysisVersionId: string;
}

/**
 * Dispatch a process run for the analysis's latest saved version. The backend
 * inserts a PROCESSING execution row and returns it; the worker finishes the run
 * out of band, which the profile query's PROCESSING poll picks up.
 */
export const useProcessAnalysisMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, analysisProfileId, analysisVersionId }: ProcessAnalysisInput) =>
      authFetch<ObjectResponse<AnalysisExecution>>(
        `/analyses/${encodeURIComponent(projectId)}` +
          `/analysis-profile/${encodeURIComponent(analysisProfileId)}/process`,
        {
          method: 'POST',
          body: JSON.stringify({ analysisVersionId }),
        }
      ),
    onSuccess: (_data, variables) =>
      markAnalysisProcessing(queryClient, variables.analysisProfileId),
    onError: (_error, variables) => {
      // The backend can commit a status change (e.g. PROCESSING -> ERROR on a
      // dispatch failure) before the error reaches us — refetch to reflect it.
      queryClient.invalidateQueries({
        queryKey: analysisKeys.profile(variables.analysisProfileId),
      });
    },
  });
};
